import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Users, Trophy, Award, TrendingUp, MapPin } from 'lucide-react';
import './CommunityPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommunityPage() {
  const { t } = useTranslation();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leaderboard?limit=10`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankMedal = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankColor = (rank) => {
    if (rank === 1) return '#fbbf24';
    if (rank === 2) return '#94a3b8';
    if (rank === 3) return '#d97706';
    return 'var(--text-secondary)';
  };

  if (loading) {
    return (
      <div className="loading-container" data-testid="community-loading">
        <div className="loading-spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="community-page" data-testid="community-page">
      <header className="page-header">
        <div>
          <h1 className="page-title" data-testid="community-title">
            <Users size={36} className="inline-icon" />
            Community
          </h1>
          <p className="page-subtitle">Entdecke die aktivsten Radfahrer in Schottland</p>
        </div>
      </header>

      {/* Hero Stats */}
      <div className="community-stats" data-testid="community-stats">
        <div className="stat-card-community">
          <div className="stat-icon-community">
            <Users size={32} />
          </div>
          <div>
            <div className="stat-value-community">{leaderboard.length}</div>
            <div className="stat-label-community">Aktive Nutzer</div>
          </div>
        </div>

        <div className="stat-card-community">
          <div className="stat-icon-community">
            <MapPin size={32} />
          </div>
          <div>
            <div className="stat-value-community">
              {Math.round(leaderboard.reduce((sum, u) => sum + u.total_distance_km, 0))}
            </div>
            <div className="stat-label-community">Gesamt-Kilometer</div>
          </div>
        </div>

        <div className="stat-card-community">
          <div className="stat-icon-community">
            <TrendingUp size={32} />
          </div>
          <div>
            <div className="stat-value-community">
              {Math.round(leaderboard.reduce((sum, u) => sum + u.total_elevation_m, 0))}
            </div>
            <div className="stat-label-community">Gesamt-Höhenmeter</div>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <section className="leaderboard-section">
        <h2 className="section-title">
          <Trophy size={28} />
          Bestenliste
        </h2>

        <div className="leaderboard-list" data-testid="leaderboard-list">
          {leaderboard.map((entry) => (
            <div 
              key={entry.user_id} 
              className={`leaderboard-entry ${entry.rank <= 3 ? 'top-three' : ''}`}
              data-testid={`leaderboard-entry-${entry.rank}`}
            >
              <div className="entry-rank" style={{ color: getRankColor(entry.rank) }}>
                <span className="rank-badge">{getRankMedal(entry.rank)}</span>
              </div>

              <div className="entry-user">
                <div className="user-avatar">
                  <Users size={24} />
                </div>
                <div className="user-info">
                  <div className="user-name">{entry.user_name}</div>
                  <div className="user-rides">{entry.total_rides} Touren</div>
                </div>
              </div>

              <div className="entry-stats">
                <div className="entry-stat">
                  <MapPin size={18} />
                  <span>{entry.total_distance_km.toFixed(1)} km</span>
                </div>
                <div className="entry-stat">
                  <TrendingUp size={18} />
                  <span>{Math.round(entry.total_elevation_m)} m</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Achievements Section */}
      <section className="achievements-showcase">
        <h2 className="section-title">
          <Award size={28} />
          Top-Erfolge der Community
        </h2>

        <div className="achievements-grid">
          <div className="achievement-showcase">
            <div className="achievement-icon-large">🏔️</div>
            <h3>Munro Master</h3>
            <p>Alle 282 Munros bestiegen</p>
            <div className="achievement-unlocked">Von 3 Nutzern erreicht</div>
          </div>

          <div className="achievement-showcase">
            <div className="achievement-icon-large">🥃</div>
            <h3>Whisky Wheelie</h3>
            <p>5 Brennereien besucht</p>
            <div className="achievement-unlocked">Von 12 Nutzern erreicht</div>
          </div>

          <div className="achievement-showcase">
            <div className="achievement-icon-large">🏝️</div>
            <h3>Island Hopper</h3>
            <p>Alle schottischen Inseln erkundet</p>
            <div className="achievement-unlocked">Von 1 Nutzer erreicht</div>
          </div>
        </div>
      </section>
    </div>
  );
}
